import { useCallback, useEffect, useRef, useState } from 'react'

import { ReportNode, useAllReportsQuery } from '@/apollo/graphql'
import { getFetchMoreStatus } from '@/utils'
import { IPageInfo } from '@/types'

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = (customerId?: number) => {
  const prevKeyword = useRef<string>('')
  const [search, setSearch] = useState<string>('')
  const [reports, setReports] = useState<Partial<ReportNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading, networkStatus } = useAllReportsQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      customer: customerId,
      text: search,
    },
    onCompleted: res => {
      if (res.allReports?.edges) {
        const _reports: Partial<ReportNode>[] = []
        res.allReports.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) _reports.push(e.node)
        })

        setReports(prev => [...prev, ..._reports])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allReports?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: reports.length }))
  }, [reports?.length])

  const onSearch = useCallback((text: string) => {
    if (prevKeyword.current !== text) {
      setReports([])
      prevKeyword.current = text
    }
    setSearch(text)
    setPageInfo(initialPageInfo)
  }, [])

  return {
    loading,
    pageInfo,
    onSearch,
    onFetchMore,
    data: reports,
  }
}
